import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { Trans, useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import Button from "@mui/material/Button";

import * as Yup from "yup";
import { CheckoutCardContent, CheckoutCardSubHeader } from "./CheckoutCard";
import { getYUPValidators } from "../../../components/yupvalidators";
import { useCheckout } from "../../providers/CheckoutProvider";
import { usePortal } from "../../../Providers/PortalProvider";
import ShippingFormUS from "./ShippingFormUS";
import ShippingFormDefault from "./ShippingFormDefault";
import List from "@mui/material/List";
import ListItemRadioButton from "../../../components/Common/ListItemRadioButton";
import { formatCurrency } from "../../../utils";
import makeStyles from "@mui/styles/makeStyles";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

const useStyles = makeStyles(() => ({
  deliveryPrice: {
    whiteSpace: "nowrap",
    fontWeight: 700,
    minWidth: "80px",
    textAlign: "right",
  },
}));

const PostageDeliveryOption = ({ deliveryMethod }) => {
  const classes = useStyles();
  const {
    user,
    options,
    deliveryOptionData,
    setDeliveryOption,
    deliveryOption,
  } = useCheckout();
  const { t } = useTranslation();
  const [currentDeliveryOption, setCurrentDeliveryOption] = useState(() => {
    if (deliveryOption && deliveryOption.type == 1) {
      return deliveryOption;
    }
    return deliveryMethod.deliveryOptions.length > 0
      ? deliveryMethod.deliveryOptions[0]
      : null;
  });
  const initialState = useMemo(() => {
    let initialState =
      deliveryOptionData?.deliveryAddress?.state || user.address.state;
    if (options.gatherState && options.states) {
      const states = Object.keys(options.states);
      if (states.indexOf(initialState) < 0) {
        initialState = "";
      }
    }
    return initialState;
  }, [options]);

  console.log("Initial state value", initialState);

  return (
    <CheckoutCardContent>
      {deliveryMethod.deliveryOptions.length > 1 && (
        <>
          <CheckoutCardContent>
            <CheckoutCardSubHeader>
              <Trans>Select shipping option</Trans>
            </CheckoutCardSubHeader>
          </CheckoutCardContent>
          <List>
            {deliveryMethod.deliveryOptions.map((deliveryOption) => {
              console.log("Delivery option", deliveryOption);
              const selected =
                deliveryOption.uuid == currentDeliveryOption?.uuid;
              const priceString =
                deliveryOption.price == 0
                  ? t("Free")
                  : formatCurrency(deliveryOption.price);
              let days = parseInt(deliveryOption.deliverytime);
              const description =
                days > 1
                  ? t("The order will be shipped within %d business days", {
                      postProcess: "sprintf",
                      sprintf: [days],
                    })
                  : t("The order will be shipped within %d business day", {
                      postProcess: "sprintf",
                      sprintf: [days],
                    });

              return (
                <ListItemRadioButton
                  labelId={"do_" + deliveryOption.uuid}
                  key={deliveryOption.uuid}
                  onClick={() => setCurrentDeliveryOption(deliveryOption)}
                  primaryText={deliveryOption.description}
                  secondaryText={description}
                  selected={selected}
                  action={
                    <Box className={classes.deliveryPrice}>{priceString}</Box>
                  }
                />
              );
            })}
          </List>
        </>
      )}
      <Formik
        initialValues={{
          name: deliveryOptionData?.deliveryAddress?.name || user.name || "",
          address:
            deliveryOptionData?.deliveryAddress?.address ||
            user.address.address ||
            "",
          address2:
            deliveryOptionData?.deliveryAddress?.address2 ||
            user.address.address2 ||
            "",
          postalCode:
            deliveryOptionData?.deliveryAddress?.postalCode ||
            user.address.postalCode ||
            "",
          city:
            deliveryOptionData?.deliveryAddress?.city ||
            user.address.city ||
            "",
          state: initialState,
          // Verify address is so we don't run verify code (client side) on an address fetched from
          // an address service.
          verifiedAddress: false,
        }}
        validateOnMount={true}
        validationSchema={schema(t, options)}
        handleChange={(e) => {
          console.log("[AUTO] The form changed!", e);
        }}
        onSubmit={(values, {}) => {
          setDeliveryOption(currentDeliveryOption, {
            deliveryAddress: values,
          });
        }}
      >
        {({ values, handleChange, isValid, setFieldValue }) => {
          return (
            <Form
              onChange={() => {
                // If we change the form, we can't assume it's verified:
                setFieldValue("verifiedAddress", false);
              }}
            >
              <CheckoutCardSubHeader>
                <Trans>Shipping address</Trans>
              </CheckoutCardSubHeader>
              <ShippingForm
                options={options}
                values={values}
                handleChange={handleChange}
              />

              <Box mt={2} mb={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.saveAddress}
                      onChange={handleChange("saveAddress")}
                      name="saveAddress"
                      color="primary"
                    />
                  }
                  label={t("Save as default address")}
                />
              </Box>

              <Box mt={2} display="flex" justifyContent="flex-end">
                <Button
                  variant="contained"
                  color="primary"
                  disabled={!isValid}
                  type="submit"
                >
                  <Trans>Next step</Trans>
                </Button>
              </Box>
            </Form>
          );
        }}
      </Formik>
    </CheckoutCardContent>
  );
};

const schema = (t, options) => () => {
  const validators = getYUPValidators(t);
  const fields = {
    name: validators.FULL_NAME.required(t("This is required!")),
    address: validators.ADDRESS.required(t("This is required!")),
    city: validators.CITY.required(t("This is required!")),
  };
  if (!options.hidePostalCode) {
    fields["postalCode"] = validators.POSTAL_CODE.required(
      t("This is required!"),
    );
  }
  if (options.gatherState) {
    fields["state"] = validators.CITY.required(t("This is required!"));
  }
  if (options.gatherAddress2) {
    // Address 2 is not required
    fields["address2"] = validators.CITY;
  }
  return Yup.object().shape(fields);
};

export default PostageDeliveryOption;

const ShippingForm = (props) => {
  const { locale } = usePortal();

  switch (locale.country.twoLetterCode) {
    // GB can use same address form as US because it's the same order,
    // just without the state field, which have to be activated for the portal
    case "GB":
    case "US":
    case "AU":
      return <ShippingFormUS {...props} />;
    default:
      return <ShippingFormDefault {...props} />;
  }
};
